import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api";
import UserSidebar from "../../components/UserSidebar";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer
} from "recharts";
import {
  FiActivity, FiCheckCircle, FiClock,
  FiPlusCircle, FiArrowRight, FiClipboard,
  FiRefreshCw, FiExternalLink, FiShield,
  FiStar, FiX
} from "react-icons/fi";

const COLORS = ["#D4AF37", "#10B981", "#3B82F6", "#EF4444", "#F59E0B", "#8B5CF6"];

const EMOJI_MAP = {
  1: { emoji: "😡", label: "Very Bad" },
  2: { emoji: "😞", label: "Bad" },
  3: { emoji: "😐", label: "Okay" },
  4: { emoji: "😊", label: "Good" },
  5: { emoji: "😍", label: "Excellent" },
};

const UserDashboard = () => {
  const navigate = useNavigate();
  const fullName = localStorage.getItem("full_name") || localStorage.getItem("username") || "Employee";

  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pendingReviewsList, setPendingReviewsList] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  // Auto Rating Popup (Mobile-like feature)
  const [pendingRatingTicket, setPendingRatingTicket] = useState(null);
  const [ratingStars, setRatingStars] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submittingRating, setSubmittingRating] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    checkPendingRating();
  }, []);

  const fetchDashboardData = async (isManual = false) => {
    if (isManual) setRefreshing(true);
    else setLoading(true);

    try {
      const [ticketsRes, catsRes, pendingReviewsRes] = await Promise.all([
        API.get("/tickets/my-tickets").catch(() => ({ data: { success: false, data: [] } })),
        API.get("/categories").catch(() => ({ data: { success: false, data: [] } })),
        API.get("/tickets/pending-reviews").catch(() => ({ data: { success: false, data: [] } })),
      ]);

      if (ticketsRes.data.success) {
        setTickets(ticketsRes.data.data || []);
      }
      if (catsRes.data.success) {
        setCategories(catsRes.data.data || []);
      }
      if (pendingReviewsRes.data.success) {
        setPendingReviewsList(pendingReviewsRes.data.data || []);
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // 🔥 Check if any resolved ticket needs rating (Matching Mobile HomeScreen behavior)
  const checkPendingRating = async () => {
    try {
      const res = await API.get("/tickets/pending-rating");
      if (res.data?.success && res.data.data) {
        setPendingRatingTicket(res.data.data);
      }
    } catch (err) {
      console.log("No pending rating check error:", err);
    }
  };

  const handleRatingSubmit = async (e) => {
    e.preventDefault();
    if (!pendingRatingTicket || ratingStars === 0) {
      alert("Please choose a star rating.");
      return;
    }

    setSubmittingRating(true);
    try {
      const res = await API.post("/tickets/submit-rating", {
        ticketId: pendingRatingTicket.id,
        ticket_id: pendingRatingTicket.id,
        rating: ratingStars,
        feedback: feedback.trim()
      });

      if (res.data?.success) {
        setPendingRatingTicket(null);
        fetchDashboardData();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to submit rating");
    } finally {
      setSubmittingRating(false);
    }
  };

  // ── Metrics Calculation ──
  const metrics = useMemo(() => {
    const total = tickets.length;
    const pending = tickets.filter(t => (t.status || "").toLowerCase() === "pending").length;
    const inProgress = tickets.filter(t => {
      const s = (t.status || "").toLowerCase();
      return s === "in progress" || s === "work in progress" || s === "in queue";
    }).length;
    const resolved = tickets.filter(t => (t.status || "").toLowerCase() === "resolved").length;
    const rate = total > 0 ? Math.round((resolved / total) * 100) : 100;

    return { total, pending, inProgress, resolved, rate };
  }, [tickets]);

  // ── Status Breakdown Chart Data ──
  const statusChartData = useMemo(() => {
    const counts = {};
    tickets.forEach(t => {
      const s = t.status || "Pending";
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.keys(counts).map(key => ({
      name: key,
      value: counts[key]
    }));
  }, [tickets]);

  // ── Top 3 Most Frequent Services / Departments ──
  const topServices = useMemo(() => {
    const counts = {};
    tickets.forEach(t => {
      const cat = t.category_name || "General";
      counts[cat] = (counts[cat] || 0) + 1;
    });

    const sorted = Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    if (sorted.length >= 3) {
      return sorted.slice(0, 3);
    }

    const existing = new Set(sorted.map(s => s.name));
    for (const c of categories) {
      if (sorted.length >= 3) break;
      if (!existing.has(c.name)) {
        sorted.push({ name: c.name, count: 0 });
      }
    }
    return sorted.slice(0, 3);
  }, [tickets, categories]);

  // ── Recent 4 Tickets ──
  const recentTickets = useMemo(() => {
    return [...tickets].slice(0, 4);
  }, [tickets]);

  return (
    <div style={s.layout}>
      <UserSidebar />

      <div style={s.main}>
        {/* ── TOP HEADER ── */}
        <div style={s.header}>
          <div>
            <div style={s.breadcrumb}>
              <FiShield size={12} color="#D4AF37" />
              <span>EMPLOYEE COMMAND DESK</span>
            </div>
            <h1 style={s.title}>Welcome back, {fullName}</h1>
            <p style={s.subtitle}>Real-time overview of your service tickets, resolution metrics, and active departments.</p>
          </div>

          <div style={s.headerActions}>
            <button
              onClick={() => fetchDashboardData(true)}
              style={s.refreshBtn}
              title="Refresh Analytics"
            >
              <FiRefreshCw size={15} style={{ animation: refreshing ? "spin 0.8s linear infinite" : "none" }} />
            </button>

            <button
              onClick={() => navigate("/user/raise-issue")}
              style={s.newTicketBtn}
            >
              <FiPlusCircle size={17} />
              <span>Raise New Issue</span>
            </button>
          </div>
        </div>

        {/* ── PENDING REVIEWS BANNER IF ANY RESOLVED UNRATED ── */}
        {pendingReviewsList.length > 0 && (
          <div style={s.pendingAlertBanner}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={s.alertIconCircle}>
                <FiStar size={18} color="#D4AF37" />
              </div>
              <div>
                <div style={s.alertTitle}>
                  You have {pendingReviewsList.length} resolved {pendingReviewsList.length === 1 ? "ticket" : "tickets"} awaiting your feedback
                </div>
                <div style={s.alertSub}>
                  Help us improve service quality by reviewing the completed work.
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setPendingRatingTicket(pendingReviewsList[0]);
                setRatingStars(0);
                setFeedback("");
              }}
              style={s.alertActionBtn}
            >
              <span>Rate Now</span>
              <FiArrowRight size={14} />
            </button>
          </div>
        )}

        {/* ── KPI METRICS CARDS ── */}
        <div style={s.statsGrid}>
          <div style={s.statCard}>
            <div style={{ ...s.statIconWrap, background: "#FFFDF5", border: "1.5px solid #F5E8BA" }}>
              <FiClipboard size={20} color="#D4AF37" />
            </div>
            <div>
              <div style={s.statLabel}>Total Raised Tickets</div>
              <div style={s.statValue}>{loading ? "..." : metrics.total}</div>
              <div style={s.statSub}>Overall Lifetime Requests</div>
            </div>
          </div>

          <div style={s.statCard}>
            <div style={{ ...s.statIconWrap, background: "#FEF3C7", border: "1.5px solid #FDE68A" }}>
              <FiClock size={20} color="#D97706" />
            </div>
            <div>
              <div style={s.statLabel}>Awaiting Action</div>
              <div style={{ ...s.statValue, color: "#D97706" }}>{loading ? "..." : metrics.pending}</div>
              <div style={s.statSub}>Pending Initial Review</div>
            </div>
          </div>

          <div style={s.statCard}>
            <div style={{ ...s.statIconWrap, background: "#EFF6FF", border: "1.5px solid #BFDBFE" }}>
              <FiActivity size={20} color="#2563EB" />
            </div>
            <div>
              <div style={s.statLabel}>In Progress</div>
              <div style={{ ...s.statValue, color: "#2563EB" }}>{loading ? "..." : metrics.inProgress}</div>
              <div style={s.statSub}>Assigned & Underway</div>
            </div>
          </div>

          <div style={s.statCard}>
            <div style={{ ...s.statIconWrap, background: "#ECFDF5", border: "1.5px solid #A7F3D0" }}>
              <FiCheckCircle size={20} color="#10B981" />
            </div>
            <div>
              <div style={s.statLabel}>Resolved Issues</div>
              <div style={{ ...s.statValue, color: "#10B981" }}>{loading ? "..." : metrics.resolved}</div>
              <div style={s.statSub}>{metrics.rate}% Resolution Rate</div>
            </div>
          </div>
        </div>

        {/* ── CHARTS & TOP SERVICES ROW ── */}
        <div style={s.visualsGrid}>
          {/* Chart 1: Status Distribution */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <div>
                <h3 style={s.cardTitle}>Tickets By Lifecycle</h3>
                <p style={s.cardSub}>Current distribution across resolution phases</p>
              </div>
              <span style={s.badgeGold}>{tickets.length} Total</span>
            </div>

            <div style={{ height: 230, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {statusChartData.length === 0 ? (
                <div style={s.emptyChart}>
                  <FiClipboard size={32} color="#CBD5E1" />
                  <p style={{ margin: "8px 0 0", fontSize: 13, color: "#A3AED0" }}>No tickets raised yet.</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {statusChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: "#1B2559", borderRadius: 10, border: "none", color: "#FFF", fontSize: 12 }}
                      itemStyle={{ color: "#D4AF37", fontWeight: 700 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Legend Indicators */}
            <div style={s.legendRow}>
              {statusChartData.map((item, idx) => (
                <div key={item.name} style={s.legendItem}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: COLORS[idx % COLORS.length] }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#475569" }}>{item.name} ({item.value})</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top 3 Services Spotlight */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <div>
                <h3 style={s.cardTitle}>Frequent Service Needs</h3>
                <p style={s.cardSub}>Your most requested operational departments</p>
              </div>
              <button
                onClick={() => navigate("/user/raise-issue")}
                style={s.linkBtn}
              >
                <span>Browse All</span>
                <FiArrowRight size={13} />
              </button>
            </div>

            <div style={s.topServicesList}>
              {topServices.map((srv, idx) => {
                const colors = [
                  { bg: "#FFFDF5", text: "#D4AF37", border: "#F5E8BA" },
                  { bg: "#EFF6FF", text: "#2563EB", border: "#BFDBFE" },
                  { bg: "#ECFDF5", text: "#10B981", border: "#A7F3D0" },
                ];
                const c = colors[idx % colors.length];

                return (
                  <div
                    key={srv.name}
                    style={s.serviceItem}
                    onClick={() => navigate("/user/raise-issue")}
                  >
                    <div style={s.serviceLeft}>
                      <div style={{ ...s.rankBadge, background: c.bg, color: c.text, borderColor: c.border }}>
                        #{idx + 1}
                      </div>
                      <div>
                        <h4 style={s.serviceName}>{srv.name}</h4>
                        <p style={s.serviceSub}>{srv.count} {srv.count === 1 ? "ticket" : "tickets"} submitted</p>
                      </div>
                    </div>

                    <div style={s.serviceAction}>
                      <span style={s.raisePill}>Quick Raise ›</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── RECENT TICKETS TABLE ── */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <div>
              <h3 style={s.cardTitle}>Recent Service Requests</h3>
              <p style={s.cardSub}>Latest issues logged under your account</p>
            </div>

            <button
              onClick={() => navigate("/user/my-tickets")}
              style={s.linkBtn}
            >
              <span>View All Raised ({tickets.length})</span>
              <FiArrowRight size={13} />
            </button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
              <thead>
                <tr style={{ borderBottom: "1.5px solid #F0F2FA", background: "#FAFCFF" }}>
                  <th style={s.th}>Ticket Code</th>
                  <th style={s.th}>Department</th>
                  <th style={s.th}>Category</th>
                  <th style={s.th}>Current Status</th>
                  <th style={s.th}>Concern Person</th>
                  <th style={s.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {recentTickets.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ padding: "40px", textAlign: "center", color: "#A3AED0", fontSize: 13 }}>
                      No service tickets found. Click "Raise New Issue" to create your first request.
                    </td>
                  </tr>
                ) : (
                  recentTickets.map(t => {
                    const statusStyles = {
                      Pending: { bg: "#FFFBEB", color: "#D97706", border: "#FDE68A" },
                      "Work in Progress": { bg: "#EFF6FF", color: "#2563EB", border: "#BFDBFE" },
                      Resolved: { bg: "#ECFDF5", color: "#10B981", border: "#A7F3D0" },
                      Rejected: { bg: "#FEF2F2", color: "#EF4444", border: "#FECACA" },
                    };
                    const st = statusStyles[t.status] || { bg: "#F8FAFC", color: "#64748B", border: "#E2E8F0" };

                    return (
                      <tr key={t.id} style={{ borderBottom: "1px solid #F8FAFC" }}>
                        <td style={s.td}>
                          <span style={s.codeBadge}>{t.ticket_code}</span>
                          <div style={s.dateSub}>
                            {new Date(t.created_at).toLocaleDateString("en-IN", {
                              day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
                            })}
                          </div>
                        </td>
                        <td style={{ ...s.td, fontWeight: 700, color: "#1B2559" }}>{t.category_name || "General"}</td>
                        <td style={{ ...s.td, color: "#475569" }}>{t.subcategory_name || "Direct Request"}</td>
                        <td style={s.td}>
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 800,
                            background: st.bg, color: st.color, border: `1px solid ${st.border}`
                          }}>
                            <span style={{ width: 6, height: 6, borderRadius: "50%", background: st.color }}></span>
                            {t.status}
                          </span>
                        </td>
                        <td style={s.td}>
                          <span style={s.userPill}>{t.hod_name || "Reviewing..."}</span>
                        </td>
                        <td style={s.td}>
                          <button
                            onClick={() => navigate("/user/my-tickets")}
                            style={s.trackBtn}
                          >
                            <span>Inspect</span>
                            <FiExternalLink size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── AUTOMATIC RESOLVED TICKET RATING MODAL (MOBILE PARITY POPUP) ── */}
        {pendingRatingTicket && (
          <div style={s.modalOverlay}>
            <div style={s.modalContent} onClick={e => e.stopPropagation()}>
              <div style={s.modalHandle}></div>

              <div style={s.modalHeader}>
                <h3 style={s.modalTitle}>Rate Your Experience</h3>
                <button 
                  style={s.modalClose} 
                  onClick={() => setPendingRatingTicket(null)}
                  title="Dismiss for now"
                >
                  <FiX size={16} />
                </button>
              </div>

              {/* TICKET DETAILS BOX (MOBILE STYLE) */}
              <div style={s.ticketDetailBox}>
                <div style={s.ticketDetailRow}>
                  <span style={s.ticketDetailLabel}>Ticket ID:</span>
                  <span style={s.ticketDetailVal}>{pendingRatingTicket.ticket_code}</span>
                </div>
                <div style={s.ticketDivider}></div>
                <div style={s.ticketDetailRow}>
                  <span style={s.ticketDetailLabel}>Issue:</span>
                  <span style={s.ticketDetailDesc}>{pendingRatingTicket.description || "Support Request"}</span>
                </div>
              </div>

              {/* INTERACTIVE 5 STARS */}
              <div style={s.starsContainer}>
                {[1, 2, 3, 4, 5].map(index => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setRatingStars(index)}
                    style={{
                      ...s.starBtn,
                      transform: ratingStars === index ? "scale(1.2)" : "scale(1.0)"
                    }}
                  >
                    <FiStar
                      size={36}
                      fill={ratingStars >= index ? "#D4AF37" : "none"}
                      color={ratingStars >= index ? "#D4AF37" : "#CBD5E1"}
                    />
                  </button>
                ))}
              </div>

              {/* EMOJI & FEELING TEXT */}
              <div style={s.emojiWrap}>
                {ratingStars === 0 ? (
                  <span style={s.tapHint}>Tap a star to rate</span>
                ) : (
                  <div style={s.emojiDisplay}>
                    <div style={s.emojiIcon}>{EMOJI_MAP[ratingStars].emoji}</div>
                    <div style={s.emojiText}>{EMOJI_MAP[ratingStars].label}</div>
                  </div>
                )}
              </div>

              {/* FEEDBACK FORM */}
              <form onSubmit={handleRatingSubmit} style={{ marginTop: 20 }}>
                <div style={{ marginBottom: 20 }}>
                  <textarea
                    rows="3"
                    value={feedback}
                    onChange={e => setFeedback(e.target.value)}
                    placeholder="Write your feedback..."
                    style={s.feedbackInput}
                  />
                </div>

                <button
                  type="submit"
                  disabled={submittingRating || ratingStars === 0}
                  style={{
                    ...s.submitBtn,
                    opacity: ratingStars === 0 || submittingRating ? 0.6 : 1,
                    cursor: ratingStars === 0 || submittingRating ? "not-allowed" : "pointer"
                  }}
                >
                  {submittingRating ? "SUBMITTING..." : "SUBMIT FEEDBACK"}
                </button>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

/* ── STYLES ─────────────────────────────────────────────── */
const s = {
  layout: { display: "flex", background: "#F8FAFC", minHeight: "100vh", fontFamily: "'Plus Jakarta Sans', sans-serif" },
  main: { marginLeft: 280, padding: "32px 36px 100px", flex: 1, minWidth: 0 },

  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22, flexWrap: "wrap", gap: 16 },
  breadcrumb: { display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#D4AF37", fontWeight: 800, letterSpacing: 0.8, marginBottom: 4 },
  title: { margin: "0 0 4px", fontSize: 24, fontWeight: 900, color: "#1B2559", letterSpacing: "-0.5px" },
  subtitle: { margin: 0, fontSize: 13, color: "#A3AED0", fontWeight: 500 },

  headerActions: { display: "flex", alignItems: "center", gap: 12 },
  refreshBtn: { width: 42, height: 42, borderRadius: 12, border: "1.5px solid #E9EDF7", background: "#FFF", color: "#A3AED0", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.18s" },
  newTicketBtn: { display: "flex", alignItems: "center", gap: 8, background: "#1B2559", color: "#D4AF37", border: "none", padding: "11px 20px", borderRadius: 12, fontSize: 13, fontWeight: 800, cursor: "pointer", letterSpacing: 0.3, boxShadow: "0 6px 16px rgba(27,37,89,0.15)", transition: "all 0.18s" },

  /* PENDING ALERT BANNER */
  pendingAlertBanner: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: "linear-gradient(90deg, #FFFDF5 0%, #FFF9E6 100%)", border: "1.5px solid #F5E8BA", borderRadius: 18, marginBottom: 24, boxShadow: "0 4px 14px rgba(212,175,55,0.08)", flexWrap: "wrap", gap: 14 },
  alertIconCircle: { width: 42, height: 42, borderRadius: 12, background: "#FFF", border: "1px solid #F5E8BA", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  alertTitle: { fontSize: 14, fontWeight: 900, color: "#1B2559" },
  alertSub: { fontSize: 12, color: "#854D0E", marginTop: 2, fontWeight: 500 },
  alertActionBtn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 18px", borderRadius: 10, background: "#1B2559", color: "#D4AF37", border: "none", fontSize: 12, fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 12px rgba(27,37,89,0.15)" },

  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 18, marginBottom: 24 },
  statCard: { background: "#FFFFFF", borderRadius: 18, padding: "18px 20px", border: "1.5px solid #F0F2FA", display: "flex", alignItems: "center", gap: 16, boxShadow: "0 2px 10px rgba(0,0,0,0.02)" },
  statIconWrap: { width: 48, height: 48, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  statLabel: { fontSize: 11, fontWeight: 800, color: "#A3AED0", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 },
  statValue: { fontSize: 22, fontWeight: 900, color: "#1B2559", lineHeight: 1.1, marginBottom: 3 },
  statSub: { fontSize: 11, color: "#94A3B8", fontWeight: 600 },

  visualsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 },
  card: { background: "#FFFFFF", borderRadius: 20, padding: "22px 24px", border: "1.5px solid #F0F2FA", boxShadow: "0 4px 16px rgba(0,0,0,0.02)", marginBottom: 20 },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 },
  cardTitle: { margin: "0 0 3px", fontSize: 16, fontWeight: 800, color: "#1B2559" },
  cardSub: { margin: 0, fontSize: 12, color: "#A3AED0", fontWeight: 500 },
  badgeGold: { padding: "4px 10px", background: "#FFFDF5", border: "1px solid #F5E8BA", borderRadius: 8, fontSize: 11, fontWeight: 800, color: "#D4AF37" },
  linkBtn: { background: "none", border: "none", color: "#D4AF37", fontSize: 12, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 },

  emptyChart: { textAlign: "center", padding: "20px 0" },
  legendRow: { display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap", marginTop: 8 },
  legendItem: { display: "flex", alignItems: "center", gap: 6 },

  topServicesList: { display: "flex", flexDirection: "column", gap: 12 },
  serviceItem: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderRadius: 14, border: "1px solid #F0F2FA", background: "#FAFCFF", cursor: "pointer", transition: "all 0.15s ease" },
  serviceLeft: { display: "flex", alignItems: "center", gap: 14 },
  rankBadge: { width: 32, height: 32, borderRadius: 10, border: "1px solid", display: "flex", alignItems: "center", justifyContent: "center" },
  serviceName: { margin: "0 0 2px", fontSize: 14, fontWeight: 800, color: "#1B2559" },
  serviceSub: { margin: 0, fontSize: 11, color: "#A3AED0", fontWeight: 600 },
  serviceAction: { display: "flex", alignItems: "center" },
  raisePill: { padding: "5px 12px", background: "#FFFDF5", border: "1px solid #F5E8BA", borderRadius: 8, fontSize: 11, fontWeight: 800, color: "#D4AF37" },

  th: { padding: "14px 18px", fontSize: 10.5, fontWeight: 800, color: "#A3AED0", textTransform: "uppercase", letterSpacing: 0.5 },
  td: { padding: "14px 18px", fontSize: 12.5, verticalAlign: "middle" },
  codeBadge: { display: "inline-block", padding: "3px 8px", background: "#F1F5F9", borderRadius: 6, fontSize: 11, fontWeight: 800, color: "#1B2559" },
  dateSub: { fontSize: 10.5, color: "#A3AED0", marginTop: 3 },
  userPill: { padding: "4px 10px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 11, fontWeight: 700, color: "#1B2559" },
  trackBtn: { padding: "6px 14px", background: "#FFFDF5", color: "#D4AF37", border: "1.5px solid #F5E8BA", borderRadius: 8, fontSize: 11, fontWeight: 800, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 },

  /* MODAL */
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  modalContent: { width: "min(460px, 100%)", background: "#FFF", borderRadius: 24, boxShadow: "0 24px 60px rgba(0,0,0,0.2)", padding: "26px 28px", boxSizing: "border-box" },
  modalHandle: { width: 44, height: 4, background: "#CBD5E1", borderRadius: 10, margin: "0 auto 16px" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 },
  modalTitle: { margin: 0, fontSize: 19, fontWeight: 900, color: "#1B2559" },
  modalClose: { background: "none", border: "none", color: "#94A3B8", cursor: "pointer", padding: 4 },

  ticketDetailBox: { background: "#FFFDF5", border: "1px solid rgba(212,175,55,0.25)", borderRadius: 16, padding: "14px 16px", marginBottom: 20 },
  ticketDetailRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  ticketDetailLabel: { fontSize: 12, color: "#64748B", fontWeight: 600 },
  ticketDetailVal: { fontSize: 13, fontWeight: 900, color: "#D4AF37" },
  ticketDivider: { height: 1, background: "rgba(212,175,55,0.15)", margin: "8px 0" },
  ticketDetailDesc: { fontSize: 12, fontWeight: 600, color: "#1B2559", maxWidth: "70%", textAlign: "right", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },

  starsContainer: { display: "flex", justifyContent: "center", gap: 10, margin: "14px 0" },
  starBtn: { background: "none", border: "none", cursor: "pointer", padding: 4, transition: "transform 0.15s" },

  emojiWrap: { textAlign: "center", minHeight: 60, display: "flex", alignItems: "center", justifyContent: "center" },
  tapHint: { fontSize: 13, color: "#94A3B8", fontWeight: 600 },
  emojiDisplay: { display: "flex", flexDirection: "column", alignItems: "center" },
  emojiIcon: { fontSize: 36, lineHeight: 1 },
  emojiText: { fontSize: 14, fontWeight: 800, color: "#D4AF37", marginTop: 4 },

  feedbackInput: { width: "100%", padding: "12px 14px", borderRadius: 14, border: "1.5px solid #F0F2FA", background: "#F8FAFC", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" },
  submitBtn: { width: "100%", padding: "14px", borderRadius: 14, border: "none", background: "#D4AF37", color: "#1B2559", fontSize: 13, fontWeight: 900, letterSpacing: 0.8, boxShadow: "0 6px 18px rgba(212,175,55,0.25)" },
};

export default UserDashboard;
